'use client';
import cn from 'classnames';
import Image from 'next/image';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useFormikContext, FormikContextType } from 'formik';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import type { StripeExpressCheckoutElementOptions } from '@stripe/stripe-js';

import './ApplicationPaymentSection.style.scss';

import AddressInputField from '@/components/form/lib/InputField/AddressInputField';
import CheckboxField from '@/components/form/lib/InputField/CheckboxField';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import Select from '@/components/form/lib/InputField/Select';
import Text from '@/components/common/Text/Text';
import analytics from '@/util/analytics';
import { APPLICATION_FLOW } from '@/const/application_flow';
import ApplePayMark from 'public/img/icon/apple-pay-mark.svg';
import GooglePayMark from 'public/img/icon/google-pay-mark.svg';
import LinkMark from 'public/img/icon/link-mark.svg';
import VisaMark from 'public/img/icon/card-visa.svg';
import MastercardMark from 'public/img/icon/card-mastercard.svg';
import AmexMark from 'public/img/icon/card-amex.svg';
import DiscoverMark from 'public/img/icon/card-discover.svg';
import { SignupState } from '@/types/Signup';
import { US_STATES } from '@/util/user_client';

import { ApplicationFormValues } from './applicationValidation';
import { applyBillingMirror } from './addressSync';
import { confirmSetupIntent } from './confirmSetupIntent';

/**
 * Runs the card path at submit time: validates the fields, creates the customer
 * and SetupIntent, then confirms. Resolves to the SetupIntent id, or throws with
 * a message suitable for display.
 */
export type ConfirmPayment = () => Promise<string>;

/**
 * How the applicant said they intend to pay. Everything except `card` is
 * collected by Stripe's own button for that wallet, shown once at the bottom
 * of the page — so these rows state an intent rather than opening anything.
 */
export type PaymentChoice = 'card' | 'apple_pay' | 'google_pay' | 'link';

/** The rows, in the order the design lists them. */
export const PAYMENT_CHOICES: { value: PaymentChoice; label: string }[] = [
  { value: 'card', label: 'Pay with credit card' },
  { value: 'apple_pay', label: 'Pay with Apple Pay' },
  { value: 'google_pay', label: 'Pay with Google Pay' },
  { value: 'link', label: 'Pay with Link' }
];

/** What the card element reports as the applicant types. */
export type PaymentStatus = {
  complete: boolean;
  /** Stripe's method type: 'card', 'apple_pay', 'google_pay', … */
  type: string | null;
};

/**
 * Human label for a payment choice, for the collapsed recap.
 *
 * `confirmedType` is what the wallet actually reported at confirm time. It wins
 * over the applicant's choice, because Link can settle as a card and the recap
 * should say what really happened.
 */
export function paymentChoiceLabel(
  choice: PaymentChoice,
  confirmedType: string | null = null
): string {
  const confirmed = PAYMENT_CHOICES.find((c) => c.value === confirmedType);

  if (confirmed && choice !== 'card') {
    return confirmed.label;
  }

  return (
    PAYMENT_CHOICES.find((c) => c.value === choice)?.label ??
    'Pay with credit card'
  );
}

/**
 * Card brand marks for the credit card row: Stripe's own copies, the same ones
 * its card field shows.
 */
const CARD_MARKS = [
  { src: VisaMark, name: 'Visa' },
  { src: MastercardMark, name: 'Mastercard' },
  { src: AmexMark, name: 'American Express' },
  { src: DiscoverMark, name: 'Discover' }
];

type WalletAvailability = Record<Exclude<PaymentChoice, 'card'>, boolean>;

/**
 * The Express Checkout options: only `only` when given (the one button the
 * applicant picked), otherwise every wallet we offer (the probe). PayPal,
 * Klarna and Amazon Pay are always off. (Link is kept out of the card fields
 * separately; see the PaymentElement below.)
 *
 * `paymentMethods` is Stripe's current name for `wallets` and the only one
 * that covers Link and the rest. It postdates our @stripe/stripe-js 3.0.3 types,
 * hence the cast; the Stripe.js script itself always loads the latest v3 and
 * honors it. Apple Pay is `'always'` because `'auto'` only offers it in
 * Safari, hiding Apple's scan-with-iPhone (QR code) flow in Chrome, Edge and
 * Firefox.
 */
export function expressCheckoutMethods(
  only?: PaymentChoice
): StripeExpressCheckoutElementOptions {
  const offer = (wallet: PaymentChoice, value: string) =>
    !only || only === wallet ? value : 'never';

  return {
    paymentMethods: {
      applePay: offer('apple_pay', 'always'),
      googlePay: offer('google_pay', 'auto'),
      link: offer('link', 'auto'),
      paypal: 'never',
      klarna: 'never',
      amazonPay: 'never'
    }
  } as unknown as StripeExpressCheckoutElementOptions;
}

/**
 * An invisible, always-mounted `ExpressCheckoutElement` whose only job is
 * asking the browser which wallets it can genuinely use, with the same
 * options as the real buttons (`expressCheckoutMethods`).
 *
 * A dedicated probe — rather than reading this off the real wallet button —
 * is what lets the radio choices below hide a wallet *before* the
 * applicant can pick one Stripe can't back, instead of after.
 */
function WalletAvailabilityProbe(props: {
  onAvailability: (availability: WalletAvailability) => void;
}) {
  const { onAvailability } = props;

  return (
    <div
      className='WalletAvailabilityProbe'
      aria-hidden='true'
    >
      <ExpressCheckoutElement
        options={expressCheckoutMethods()}
        onReady={({ availablePaymentMethods }) =>
          onAvailability({
            apple_pay: !!availablePaymentMethods?.applePay,
            google_pay: !!availablePaymentMethods?.googlePay,
            // Missing from our 3.0.3 types; Stripe.js reports it.
            link: !!(availablePaymentMethods as { link?: boolean } | undefined)
              ?.link
          })
        }
        // Required by the type; this element is hidden and never clicked, so
        // it never actually fires.
        onConfirm={() => {}}
      />
    </div>
  );
}

type ApplicationPaymentSectionProps = {
  user: SignupState;
  choice: PaymentChoice;
  onChoiceChange: (choice: PaymentChoice) => void;
  /** Handed the confirm function once the card element is usable. */
  onConfirmReady: (confirm: ConfirmPayment | null) => void;
  /** Completeness of the card fields, from the element's own onChange. */
  onStatusChange: (status: PaymentStatus) => void;
  /**
   * Whether the Billing Method section is actually open. `CollapsibleSection`
   * keeps this mounted even while collapsed (Stripe wipes the card input if it
   * unmounts), so `PaymentElement` can render its first layout inside a
   * zero-height, `overflow: hidden` box — Stripe's iframe caches whatever
   * height it measured, and Link's own inline card offer arriving later never
   * gets a fresh remeasure, clipping it. Nudges Stripe to remeasure once the
   * section is genuinely visible.
   */
  isOpen: boolean;
  /**
   * A wallet whose button failed to render at submit, despite the probe. Its
   * row is hidden and the applicant is asked to pick another option.
   */
  unavailableChoice: PaymentChoice | null;
  /**
   * Show the billing address next to a wallet too. Set after the wallet's own
   * address was refused, so the member can correct the one we send instead.
   */
  showWalletBilling: boolean;
};

/**
 * "Billing Method": how the applicant intends to pay, and the card fields when
 * that is by card.
 *
 * The wallets are not collected here. Apple requires its own button to open the
 * payment sheet, and the sheet both authorizes and submits in one gesture — so
 * choosing a wallet here is a statement of intent, and the actual Apple Pay or
 * Google Pay button lives at the bottom of the page next to the consents it has
 * to come after. See `ApplicationWalletButtons`.
 *
 * Lives inside the page's `<Elements>` provider, so `useStripe`/`useElements`
 * resolve here without a provider of its own.
 */
function ApplicationPaymentSection(props: ApplicationPaymentSectionProps) {
  const {
    user,
    choice,
    onChoiceChange,
    onConfirmReady,
    onStatusChange,
    isOpen,
    unavailableChoice,
    showWalletBilling
  } = props;

  const stripe = useStripe();
  const elements = useElements();
  const { values, setFieldValue } = useFormikContext<ApplicationFormValues>();

  /**
   * The billing address fields are card-only and hidden for a wallet, but
   * their validation isn't — an applicant who unticked "same as my shipping
   * address" on the card path and then picked a wallet was stuck: Billing
   * never counted as complete, so the review block and wallet buttons never
   * appeared. A wallet carries its own billing details, so re-tick it.
   */
  const handleChoiceChange = (value: PaymentChoice) => {
    if (value !== 'card') {
      setFieldValue('sameAsShippingAddress', true);
    }
    // Only the applicant's own picks — the automatic fall-backs to card below
    // call `onChoiceChange` directly and stay out of this.
    analytics.track('Application - Payment Method Selected', {
      application_flow: APPLICATION_FLOW.SINGLE_PAGE,
      payment_method: value
    });
    onChoiceChange(value);
  };

  // Refs so the effect below does not re-run — and hand a fresh confirm function
  // upward — on every keystroke, while still reading current values.
  const onConfirmReadyRef = useRef(onConfirmReady);
  onConfirmReadyRef.current = onConfirmReady;

  const applicantRef = useRef<SignupState>(user);
  applicantRef.current = { ...user, ...applyBillingMirror(values) };

  /**
   * `elements.update` takes no real option changes here — passing the same
   * options back is just the documented way to make Stripe recompute each
   * mounted Element's layout. The section's own 0.35s expand transition
   * needs to actually finish first, or this measures the still-collapsing
   * box.
   */
  useEffect(() => {
    if (!isOpen || !elements) {
      return;
    }

    const timeout = setTimeout(() => elements.update({}), 400);

    return () => clearTimeout(timeout);
  }, [isOpen, elements]);

  useEffect(() => {
    if (!stripe || !elements) {
      onConfirmReadyRef.current(null);
      return;
    }

    onConfirmReadyRef.current(() =>
      confirmSetupIntent({ stripe, elements, applicant: applicantRef.current })
    );

    return () => onConfirmReadyRef.current(null);
  }, [stripe, elements]);

  /**
   * `null` while the probe hasn't reported yet. Both wallet rows stay hidden
   * for that first moment rather than flashing in and then possibly back out
   * — appearing once confirmed reads better than appearing and disappearing.
   */
  const [walletAvailability, setWalletAvailability] =
    useState<WalletAvailability | null>(null);

  // The applicant can't still be sitting on a wallet the probe has just ruled
  // out — a restored session (`sessionStorage`) is the one way `choice` could
  // already be a wallet before availability is known.
  const isOfferable = (value: PaymentChoice) =>
    value === 'card' ||
    (value !== unavailableChoice && !!walletAvailability?.[value]);

  useEffect(() => {
    if (walletAvailability && !isOfferable(choice)) {
      onChoiceChange('card');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAvailability, choice, onChoiceChange]);

  return (
    <div
      className='ApplicationPaymentSection'
      data-testid='payment-fields'
    >
      {/*
        Unmounts itself the moment it has an answer. Stripe allows only one
        `expressCheckout` Element per `<Elements>` provider — this page's is
        shared, so a probe left mounted collides with `ApplicationWalletButtons`'
        own ExpressCheckoutElement once the review block appears, and Stripe
        throws ("Can only create one Element of type expressCheckout"),
        crashing the page. One answer is all the probe is for; it has no
        reason to still be here afterward.
      */}
      {walletAvailability === null && (
        <WalletAvailabilityProbe onAvailability={setWalletAvailability} />
      )}

      {unavailableChoice && (
        <div
          className='ApplicationPaymentSection-unavailable'
          role='alert'
          data-testid='payment-unavailable'
        >
          <Text variant='text-m'>
            {paymentChoiceLabel(unavailableChoice).replace('Pay with ', '')}{' '}
            isn&apos;t available on this device. Please select another option.
          </Text>
        </div>
      )}

      <div
        className='ApplicationPaymentSection-choices'
        role='radiogroup'
        aria-label='Billing method'
      >
        {PAYMENT_CHOICES.filter(({ value }) => isOfferable(value)).map(
          ({ value, label }) => (
            <Fragment key={value}>
              <label
                className={cn('ApplicationPaymentSection-choice cursor', {
                  selected: choice === value
                })}
              >
                <input
                  type='radio'
                  name='paymentChoice'
                  value={value}
                  checked={choice === value}
                  onChange={() => handleChoiceChange(value)}
                  data-testid={`payment-choice-${value}`}
                />
                <Text variant='title-xxs-500-upper'>{label}</Text>
                {/* Official marks, unaltered, at equal height (brand rules). */}
                {value === 'apple_pay' && (
                  <Image
                    className='ApplicationPaymentSection-mark'
                    src={ApplePayMark}
                    alt=''
                    height={24}
                    width={37}
                  />
                )}
                {value === 'google_pay' && (
                  <Image
                    className='ApplicationPaymentSection-mark'
                    src={GooglePayMark}
                    alt=''
                    height={24}
                    width={45}
                  />
                )}
                {value === 'link' && (
                  // Link's symbol, taken from link.com's own markup.
                  <Image
                    className='ApplicationPaymentSection-mark'
                    src={LinkMark}
                    alt=''
                    height={24}
                    width={24}
                  />
                )}
                {value === 'card' && (
                  <span className='ApplicationPaymentSection-mark ApplicationPaymentSection-cards'>
                    {CARD_MARKS.map(({ src, name }) => (
                      <Image
                        key={name}
                        src={src}
                        alt=''
                        height={24}
                        width={36}
                      />
                    ))}
                  </span>
                )}
              </label>

              {/*
              The card fields open right under their own row, above the
              wallets. A wallet carries its own billing details, so the
              billing address is card-only too.
            */}
              {value === 'card' && choice === 'card' && (
                <div className='ApplicationPaymentSection-card-fields'>
                  <PaymentElement
                    options={{
                      layout: 'tabs',
                      // Collected by `BillingAddressFields` below instead,
                      // so the card form does not ask for an address the
                      // member may have given.
                      fields: { billingDetails: 'never' },
                      // The wallet buttons (Link included) live at the bottom
                      // of the page, so the card fields must not offer their
                      // own. `link` is missing from our 3.0.3 types, hence
                      // the cast.
                      // ponytail: Link inline here drags in a Card/Bank tab
                      // (Link Instant Bank Payments), which only Stripe
                      // support can turn off. Once it is off, drop
                      // `link: 'never'` and the Link radio to autofill inline
                      // instead of via Link's separate-tab button.
                      wallets: {
                        applePay: 'never',
                        googlePay: 'never',
                        link: 'never'
                      } as { applePay: 'never'; googlePay: 'never' }
                    }}
                    onChange={(event) =>
                      onStatusChange({
                        complete: event.complete,
                        type: event.value?.type ?? null
                      })
                    }
                  />
                  <BillingAddressFields />
                </div>
              )}
            </Fragment>
          )
        )}
      </div>

      {choice !== 'card' && (
        <Text
          variant='text-m'
          className='ApplicationPaymentSection-wallet-note'
          data-testid='payment-wallet-note'
        >
          You&apos;ll approve the payment with{' '}
          {paymentChoiceLabel(choice).replace('Pay with ', '')} when you submit
          your application.
        </Text>
      )}

      {choice !== 'card' && showWalletBilling && <BillingAddressFields />}
    </div>
  );
}

/**
 * The billing address, with "same as my shipping address" ticked by default.
 *
 * The multi-page flow has always collected this separately (`SignupStep4Form`),
 * and members whose card bills somewhere other than where they live need it:
 * without it their billing ZIP is the shipping one, which fails AVS.
 */
function BillingAddressFields() {
  const { values } = useFormikContext<ApplicationFormValues>();

  const handleBillingSelected = (
    formikContext: FormikContextType<unknown>,
    formattedAddress: string
  ) => {
    const parts = (formattedAddress || '').split(',');
    const regionAndPostcode = parts[2]?.trim().split(' ') ?? [];

    formikContext.setFieldValue('billingAddress', parts[0] || '', false);
    formikContext.setFieldValue('billingCity', parts[1]?.trim() || '', false);
    formikContext.setFieldValue(
      'billingState',
      regionAndPostcode[0] || '',
      false
    );
    formikContext.setFieldValue(
      'billingPostalCode',
      regionAndPostcode[1] || '',
      false
    );
  };

  return (
    <div className='ApplicationBillingAddress'>
      <div className='InputField-checkbox'>
        <CheckboxField
          name='sameAsShippingAddress'
          className='Signup-same-as-shipping'
          label='My billing address is the same as my shipping address'
        />
      </div>

      {!values.sameAsShippingAddress && (
        <div className='Signup-billing-address'>
          <h3 className='Signup-subtitle'>Billing Address</h3>
          <AddressInputField
            className='Signup-address'
            label='Address'
            onAddressSelected={handleBillingSelected}
            name='billingAddress'
            defaultValue={values.billingAddress}
          />
          <div className='Signup-row'>
            <InputField
              className='Signup-apt-unit-suite'
              name='billingAptUnitSuite'
              autoComplete='billing address-line2'
              label='apt/unit/suite (optional)'
              type='text'
            />
            <InputField
              className='Signup-city'
              name='billingCity'
              autoComplete='billing address-level2'
              label='City'
              type='text'
            />
            <Select
              name='billingState'
              autoComplete='billing address-level1'
              label='State'
              options={US_STATES}
            />
            <MaskedField
              id='billingPostalCode'
              className='Signup-postal-code'
              guide={false}
              mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
              label='Postal Code'
              name='billingPostalCode'
              autoComplete='billing postal-code'
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationPaymentSection;
